import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import {
  PortalFallback,
  RedirectIfAuthenticated,
  RequirePersonal,
  RequireStudent,
} from './guards'

/**
 * Code splitting por área. O bundle do portal não carrega o painel:
 * o aluno na academia não baixa a tela de financeiro do personal.
 */
const LoginPage = lazy(() => import('../panel/auth/LoginPage'))
const RegisterPage = lazy(() => import('../panel/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() =>
  import('../panel/auth/PasswordPages').then((m) => ({ default: m.ForgotPasswordPage })),
)
const ResetPasswordPage = lazy(() =>
  import('../panel/auth/PasswordPages').then((m) => ({ default: m.ResetPasswordPage })),
)
const PanelShell = lazy(() => import('../panel/PanelShell'))
const DashboardPage = lazy(() => import('../panel/DashboardPage'))
const BrandingPage = lazy(() => import('../panel/BrandingPage'))
const WhatsappPage = lazy(() => import('../panel/WhatsappPage'))
const StudentsPage = lazy(() => import('../panel/students/StudentsPage'))
const StudentProfilePage = lazy(() => import('../panel/students/StudentProfilePage'))
const ExercisesPage = lazy(() => import('../panel/exercises/ExercisesPage'))
const WorkoutsPage = lazy(() => import('../panel/workouts/WorkoutsPage'))
const FinancialPage = lazy(() => import('../panel/financial/FinancialPage'))
const AgendaPage = lazy(() => import('../panel/appointments/AgendaPage'))

const TenantBoot = lazy(() =>
  import('../portal/TenantBoot').then((m) => ({ default: m.TenantBoot })),
)
const PortalLoginPage = lazy(() => import('../portal/PortalLoginPage'))
const FirstAccessPage = lazy(() => import('../portal/FirstAccessPage'))
const PortalShell = lazy(() => import('../portal/PortalShell'))
const PortalHomePage = lazy(() => import('../portal/PortalHomePage'))
const PortalEvolutionPage = lazy(() => import('../portal/PortalEvolutionPage'))
const PortalFinancialPage = lazy(() => import('../portal/PortalFinancialPage'))
const PortalWorkoutsPage = lazy(() => import('../portal/PortalWorkoutsPage'))
const PortalWorkoutDetailPage = lazy(() =>
  import('../portal/PortalWorkoutsPage').then((m) => ({ default: m.PortalWorkoutDetailPage })),
)
const PortalHistoryPage = lazy(() =>
  import('../portal/PortalWorkoutsPage').then((m) => ({ default: m.PortalHistoryPage })),
)
const PortalProfilePage = lazy(() => import('../portal/PortalProfilePage'))
const WorkoutExecutionPage = lazy(() => import('../portal/WorkoutExecutionPage'))

function Fallback(): ReactNode {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg">
      <span className="sr-only">Carregando</span>
      <div aria-hidden className="size-10 animate-pulse rounded-xl bg-surface-sunken" />
    </div>
  )
}

const router = createBrowserRouter([
  // ── Painel do personal ──────────────────────────────────────
  {
    element: <RedirectIfAuthenticated />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/cadastro', element: <RegisterPage /> },
      { path: '/recuperar-senha', element: <ForgotPasswordPage /> },
      { path: '/redefinir-senha', element: <ResetPasswordPage /> },
    ],
  },
  {
    element: <RequirePersonal />,
    children: [
      {
        element: <PanelShell />,
        children: [
          { path: '/dashboard', element: <DashboardPage /> },
          { path: '/alunos', element: <StudentsPage /> },
          { path: '/alunos/:id', element: <StudentProfilePage /> },
          { path: '/exercicios', element: <ExercisesPage /> },
          { path: '/treinos', element: <WorkoutsPage /> },
          { path: '/financeiro', element: <FinancialPage /> },
          { path: '/agenda', element: <AgendaPage /> },
          { path: '/configuracoes/marca', element: <BrandingPage /> },
          { path: '/configuracoes/whatsapp', element: <WhatsappPage /> },
        ],
      },
    ],
  },

  // ── PWA do aluno, com escopo por slug ───────────────────────
  {
    path: '/app/:slug',
    element: <TenantBoot />,
    children: [
      { path: 'login', element: <PortalLoginPage /> },
      { path: 'primeiro-acesso', element: <FirstAccessPage /> },
      // Recuperação de senha DENTRO do app do aluno: o link que chega por
      // e-mail aponta para cá, e "Esqueci minha senha" no login do portal
      // apontava para uma rota que não existia — caindo no fallback, que
      // devolvia o aluno ao próprio login sem explicar nada.
      { path: 'recuperar-senha', element: <ForgotPasswordPage /> },
      { path: 'redefinir-senha', element: <ResetPasswordPage /> },
      // Antes de qualquer autenticação: rota errada no app do aluno volta
      // para o app dele, nunca para o painel do personal.
      { path: '*', element: <PortalFallback /> },
      {
        element: <RequireStudent />,
        children: [
          // FocusShell: fora do PortalShell, sem header nem bottom nav.
          { path: 'treinos/:id/executar', element: <WorkoutExecutionPage /> },
          {
            element: <PortalShell />,
            children: [
              { index: true, element: <PortalHomePage /> },
              { path: 'evolucao', element: <PortalEvolutionPage /> },
              { path: 'treinos', element: <PortalWorkoutsPage /> },
              { path: 'treinos/:id', element: <PortalWorkoutDetailPage /> },
              { path: 'historico', element: <PortalHistoryPage /> },
              { path: 'financeiro', element: <PortalFinancialPage /> },
              { path: 'perfil', element: <PortalProfilePage /> },
            ],
          },
        ],
      },
    ],
  },

  { path: '/', element: <Navigate to="/dashboard" replace /> },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
])

export function AppRouter(): ReactNode {
  return (
    <Suspense fallback={<Fallback />}>
      <RouterProvider router={router} />
    </Suspense>
  )
}
